import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormFieldDefinition, IntakeFormTemplate } from '../../../types/formBuilder';

interface DynamicFormRendererProps {
  formTemplate: IntakeFormTemplate;
  initialValues?: { [fieldName: string]: any };
  onSubmit: (answers: { [fieldName: string]: any }) => void;
  submitButtonText?: string;
  isSubmitting?: boolean;
  readOnly?: boolean;
}

export function DynamicFormRenderer({ 
  formTemplate, 
  initialValues = {}, 
  onSubmit,
  submitButtonText,
  isSubmitting = false,
  readOnly = false
}: DynamicFormRendererProps) {
  const { t, i18n } = useTranslation();
  const [answers, setAnswers] = useState<{ [fieldName: string]: any }>(initialValues);

  useEffect(() => {
    setAnswers(initialValues);
  }, [initialValues]);

  const handleChange = (fieldName: string, value: any) => {
    setAnswers(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields: string[] = [];
    formTemplate.fields.forEach(field => {
      if (field.required) {
        const value = answers[field.field_name];
        if (value === undefined || value === null || value === '') {
          missingFields.push(field.label);
        }
      }
    });

    if (missingFields.length > 0) {
      alert(`${t('formRenderer.requiredFields')}: ${missingFields.join(', ')}`);
      return;
    }

    onSubmit(answers);
  };

  const renderField = (field: FormFieldDefinition) => {
    const value = answers[field.field_name] ?? '';

    // Check conditional logic
    if (field.conditionalLogic?.showIf) {
      const condition = field.conditionalLogic.showIf;
      const conditionValue = answers[condition.field];
      
      let shouldShow = false;
      switch (condition.operator) {
        case 'equals':
          shouldShow = conditionValue === condition.value;
          break;
        case 'notEquals':
          shouldShow = conditionValue !== condition.value;
          break;
        case 'contains':
          shouldShow = String(conditionValue || '').includes(String(condition.value));
          break;
        case 'greaterThan':
          shouldShow = Number(conditionValue) > Number(condition.value);
          break;
        case 'lessThan':
          shouldShow = Number(conditionValue) < Number(condition.value);
          break;
      }
      
      if (!shouldShow) return null;
    }

    const fieldStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      marginBottom: 16
    };

    const inputStyle: React.CSSProperties = {
      padding: '10px 12px',
      border: '1px solid #ced4da',
      borderRadius: 6,
      fontSize: '14px'
    };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <div key={field.id} style={fieldStyle}>
            <label htmlFor={field.field_name} style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <input
              id={field.field_name}
              type={field.type}
              value={String(value)}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              placeholder={field.placeholder}
              style={inputStyle}
              disabled={readOnly}
              readOnly={readOnly}
            />
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} style={fieldStyle}>
            <label htmlFor={field.field_name} style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <textarea
              id={field.field_name}
              value={String(value)}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              placeholder={field.placeholder}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
              disabled={readOnly}
              readOnly={readOnly}
            />
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} style={fieldStyle}>
            <label htmlFor={field.field_name} style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <input
              id={field.field_name}
              type="number"
              value={value}
              onChange={(e) => handleChange(field.field_name, Number(e.target.value))}
              placeholder={field.placeholder}
              min={field.validation?.min}
              max={field.validation?.max}
              style={inputStyle}
              disabled={readOnly}
              readOnly={readOnly}
            />
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'date':
        return (
          <div key={field.id} style={fieldStyle}>
            <label htmlFor={field.field_name} style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <input
              id={field.field_name}
              type="date"
              value={String(value)}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              style={inputStyle}
              disabled={readOnly}
              readOnly={readOnly}
            />
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} style={fieldStyle}>
            <label htmlFor={field.field_name} style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <select
              id={field.field_name}
              value={String(value)}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              style={inputStyle}
              disabled={readOnly}
            >
              <option value="">--</option>
              {(field.options || []).map((opt, idx) => (
                <option key={idx} value={opt}>{opt}</option>
              ))}
            </select>
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div key={field.id} style={fieldStyle}>
            <label style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(field.options || []).map((opt, idx) => (
                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(opt)}
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...selectedValues, opt]
                        : selectedValues.filter(v => v !== opt);
                      handleChange(field.field_name, newValues);
                    }}
                    style={{ width: '16px', height: '16px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    disabled={readOnly}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} style={fieldStyle}>
            <label style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(field.options || []).map((opt, idx) => (
                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name={field.field_name}
                    value={opt}
                    checked={value === opt}
                    onChange={(e) => handleChange(field.field_name, e.target.value)}
                    style={{ width: '16px', height: '16px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    disabled={readOnly}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} style={fieldStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => handleChange(field.field_name, e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                disabled={readOnly}
              />
              <span style={{ fontWeight: 500 }}>
                {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
              </span>
            </label>
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'yes_no':
        return (
          <div key={field.id} style={fieldStyle}>
            <label style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={field.field_name}
                  value="yes"
                  checked={value === 'yes'}
                  onChange={(e) => handleChange(field.field_name, e.target.value)}
                  style={{ width: '16px', height: '16px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  disabled={readOnly}
                />
                <span>{t('common.yes')}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={field.field_name}
                  value="no"
                  checked={value === 'no'}
                  onChange={(e) => handleChange(field.field_name, e.target.value)}
                  style={{ width: '16px', height: '16px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  disabled={readOnly}
                />
                <span>{t('common.no')}</span>
              </label>
            </div>
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      case 'scale':
        const scaleMax = field.validation?.max || 10;
        const scaleMin = field.validation?.min || 1;
        return (
          <div key={field.id} style={fieldStyle}>
            <label style={{ fontWeight: 500 }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{scaleMin}</span>
              <input
                type="range"
                min={scaleMin}
                max={scaleMax}
                value={Number(value) || scaleMin}
                onChange={(e) => handleChange(field.field_name, Number(e.target.value))}
                style={{ flex: 1 }}
                disabled={readOnly}
              />
              <span>{scaleMax}</span>
              <span style={{ 
                minWidth: '40px', 
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
                {Number(value) || scaleMin}
              </span>
            </div>
            {field.description && <small style={{ color: '#666' }}>{field.description}</small>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ 
      background: '#fff', 
      border: '1px solid #e9ecef', 
      borderRadius: 10, 
      padding: 20,
      direction: i18n.language === 'he' ? 'rtl' : 'ltr',
      textAlign: i18n.language === 'he' ? 'right' : 'left'
    }}>
      {formTemplate.fields
        .sort((a, b) => a.order - b.order)
        .map(field => renderField(field))}

      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, paddingTop: 20, borderTop: '1px solid #e9ecef' }}>
          <button 
            type="submit" 
            disabled={isSubmitting}
            style={{ 
              background: isSubmitting ? '#ccc' : '#007acc', 
              color: '#fff', 
              border: 'none', 
              padding: '12px 24px', 
              borderRadius: 6, 
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: isSubmitting ? 0.7 : 1
            }}
          >
            {submitButtonText || t('intake.save')}
          </button>
        </div>
      )}
    </form>
  );
}

export default DynamicFormRenderer;

